import type { Me, OrgRole, ProjectRole } from '@hangar/shared';

const nameSourceOf = (name: string | null | undefined, email?: string): string => name?.trim() || email?.split('@')[0] || '';

/** "José Zamora" → "JZ"; falls back to the first two letters of an email. */
export function initials(name: string | null | undefined, email?: string): string {
  const parts = nameSourceOf(name, email).split(/[\s._-]+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : (parts[0]?.[1] ?? '');
  return (first + last).toUpperCase() || '?';
}

const ORG_ROLE_LABEL: Record<OrgRole, string> = { owner: 'Owner', admin: 'Admin', member: 'Member', guest: 'Guest', viewer: 'Viewer' };
const PROJECT_ROLE_LABEL: Record<ProjectRole, string> = {
  maintainer: 'Maintainer',
  release_manager: 'Release manager',
  developer: 'Developer',
  client: 'Client',
  viewer: 'Viewer',
};

/** Role line for the sidebar footer: org role, or the first project role for external users. */
export function roleLabel(me: Me): string {
  if (me.orgRole) return ORG_ROLE_LABEL[me.orgRole];
  const first = me.projects[0];
  return first ? PROJECT_ROLE_LABEL[first.role] : me.actor.type === 'user' ? 'Member' : me.actor.type;
}

/** Full date-time strings (createdAt, occurredAt) may go through Date; date-only values never do (ground rule 3). */
export function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(d);
}

export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(d);
}

export function timeAgo(iso: string, now: number = Date.now()): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 5) return 'just now';
  if (s < 60) return `${s} s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  return `${d} d ago`;
}

export const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,62}$/;
export const SLUG_HINT = 'Lowercase letters, digits and hyphens · 2–63 characters';

/** "StoreAI Mobile" → "storeai-mobile" (suggestion only; the user can override). */
export function slugify(input: string): string {
  return input
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);
}

/** "3f9c2b7e…" → "3f9c2b7" (7 chars, like the design's commit chips). */
export const shortSha = (sha: string | null | undefined): string => (sha ? sha.slice(0, 7) : '');

/** 4 812 → "1h 20m", 195 → "3m 15s", 42 → "42s". */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return '—';
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const rest = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${rest.toString().padStart(2, '0')}s`;
  return `${rest}s`;
}

/** Seconds between two instants (the second defaults to now) for live durations. */
export function elapsedSeconds(from: string | null | undefined, to?: string | null, now: number = Date.now()): number | null {
  if (!from) return null;
  const a = new Date(from).getTime();
  const b = to ? new Date(to).getTime() : now;
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.max(0, (b - a) / 1000);
}

/** 48 213 904 → "48.2 MB". */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let v = bytes / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
}

/** "in 12 min" / "3 min ago" countdowns (deadlines). */
export function timeUntil(iso: string, now: number = Date.now()): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const s = Math.round((t - now) / 1000);
  if (s <= 0) return 'expired';
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ${(s % 60).toString().padStart(2, '0')} s`;
  const h = Math.floor(m / 60);
  return `${h} h ${m % 60} min`;
}

export const capitalize = (s: string): string => (s ? s[0]!.toUpperCase() + s.slice(1) : s);

/** "pending_approval" → "Pending approval" (labels for enum values). */
export const humanize = (s: string): string => capitalize(s.replace(/_/g, ' '));

/** Best-effort copy to the clipboard (returns false when the API is unavailable, e.g. insecure contexts). */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
